/**
 * "Report" beside an error: one click sends it, and the label reads "Reported" only once the report was taken.
 * Nothing is typed. What is sent is explained behind the info icon. It renders nothing for an error that
 * can't be reported (offline, a dropped connection, a signed-out session — and, for a guest, a refusal).
 */
import { useState } from 'react';
import { Button } from './Button';
import { INFO_TARGET_RIGHT, InfoTip } from './InfoTip';
import { useToast } from './Toast';
import { cn } from '@/lib/cn';
import { pinFailure, type Failure } from '@/lib/failures';
import { canReport, reportProblem, reporterSignedIn, submitReport, type ReportSurface } from '@/lib/report';
import { loadFailure } from '@/lib/write';

type State = 'idle' | 'sending' | 'done';

export function ReportControl({ surface, failure, look = 'link', side = 'bottom', align = 'start', eventId, className, onStart, onSettled }: {
  surface: ReportSurface;
  failure: Omit<Failure, 'at'> & { at?: number };
  look?: 'link' | 'ghost';
  /** Where the explanation opens on hover, and which edge of the control it lines up with. */
  side?: 'bottom' | 'top';
  align?: 'start' | 'end';
  /** The event the error belongs to, when the page's address doesn't carry it. */
  eventId?: string | null;
  /** `static` hands the explanation's position to a wider positioned parent (a centred row). */
  className?: string;
  onStart?: () => void;
  onSettled?: (reported: boolean) => void;
}) {
  const toast = useToast();
  const [state, setState] = useState<State>('idle');
  const f: Failure = { ...failure, at: failure.at ?? 0 };
  if (!canReport(f)) return null;
  const press = async () => {
    if (state !== 'idle') return;
    setState('sending');
    onStart?.();
    const outcome = await submitReport(surface, f, { eventId });
    const reported = outcome === 'sent';
    setState(reported ? 'done' : 'idle');
    if (outcome === 'sent') toast.announce('Reported');
    else toast.error(reportProblem(outcome), 'report-failed');
    onSettled?.(reported);
  };
  const label = state === 'done' ? 'Reported' : 'Report';
  const common = { 'data-testid': 'report-button', 'data-report-state': state, disabled: state !== 'idle', 'aria-busy': state === 'sending' || undefined, onClick: () => void press() } as const;
  return (
    <span className={cn('relative inline-flex items-center gap-1', className)} data-report-control>
      {look === 'ghost' ? (
        <Button variant="ghost" {...common}>{label}</Button>
      ) : (
        <button type="button" {...common}
          className={cn('inline-flex min-h-6 items-center rounded-control font-semibold text-ink underline decoration-brand decoration-2 underline-offset-2 disabled:cursor-not-allowed',
            state === 'done' && 'text-ink-2 no-underline', state === 'sending' && 'opacity-55')}>
          {label}
        </button>
      )}
      {/* the icon's 44 px touch target reaches right, away from the word beside it, so Report keeps its whole box */}
      <InfoTip title="What a report sends" side={side} align={align} className={cn('static', INFO_TARGET_RIGHT)}>
        {reporterSignedIn()
          ? "This message, the page you're on with record ids removed, what the app was doing, the app version, your browser and window size, and your account, role and organization — sent to the Room Map Pro team. Passwords and card details are never sent; email addresses and phone numbers are removed."
          : "The page you're on with its private link removed, the app version, your browser and window size — sent to the Room Map Pro team. Nothing you typed is sent."}
      </InfoTip>
    </span>
  );
}

/**
 * Report for an alert shown in place. It appears only when the sentence on screen is one a failed save or server
 * call produced; a field check at the same spot shows nothing.
 */
export function InlineReport({ message, className }: { message: string | null | undefined; className?: string }) {
  // held here once found: the control stays beside its sentence however many failures come after it
  const [pinned, setPinned] = useState<Failure | null>(null);
  const failure = pinFailure(pinned, message);
  if (failure !== pinned) setPinned(failure);
  if (!failure || !canReport(failure)) return null;
  return (
    <div className={cn('relative flex min-w-0 text-sm', className)} data-testid="inline-report">
      {/* an alert usually sits at the foot of a card or dialog: the explanation opens upward, where there is room */}
      <ReportControl key={failure.at} surface="inline" failure={failure} side="top" className="static" />
    </div>
  );
}

/**
 * Report beside a list, a preview or a detail that could not be loaded and says so in place. `message` is the fixed
 * sentence; the server's own words travel as the detail. A dropped connection shows nothing.
 */
export function LoadReport({ message, error, apiPath, className }: { message: string; error: unknown; apiPath?: string; className?: string }) {
  const failure = loadFailure(message, error, apiPath);
  if (!failure) return null;
  return (
    <span className={cn('relative flex min-w-0 text-sm', className)} data-testid="inline-report">
      <ReportControl surface="inline" failure={failure} side="top" className="static" />
    </span>
  );
}
